import * as dayjs from 'dayjs';
import { LIBRARY_TYPES, SORT_OPTIONS } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { FullstoryService, fullstoryToken } from 'go-modules/services/fullstory/fullstory.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { LibraryCollectionFolderItemCount, LibraryService } from '@go-modules/services/library/library.service';
import { MessageDialogComponent, MessageDialogData } from '../../dialogs/message-dialog/message-dialog.component';
import { SearchPipe } from '@go-modules/pipes/search/search.pipe';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { ManageLibraryCollectionFolderComponent } from '../manage-library-collection-folder/manage-library-collection-folder.component';
import type { LibraryCollection, LibraryFolder, LibraryCollectionItem, LibraryCollectionItemsAndFolders } from '@go-modules/interfaces/library/library-collection';
import type { ContentTypeType } from '@go-modules/interfaces/library/library-types';
import { LibraryTreeDataSource } from '@go-modules/components/library/library-tree-viewer/library-tree-datasource';

@Component({
	selector: 'ngx-collection-folder-viewer',
	templateUrl: './collection-folder-viewer.component.html',
	styleUrls: ['./collection-folder-viewer.component.scss'],
	standalone: false
})
export class CollectionFolderViewerComponent implements OnInit, OnChanges {
	public filteredItems: LibraryCollectionItem[] = [];
	@Input() public folder: LibraryFolder;
	@Input() public expanded: string[];
	@Input() public expandedFolders: any[];
	@Input() public mode: string;
	@Output() public selectItem: EventEmitter<any> = new EventEmitter();
	@Input() public collections: LibraryCollection[];
	@Input() public archiveCollection: LibraryCollection;
	@Input() public selectedCollection: LibraryCollection;
	@Input() public selectedContentType: ContentTypeType;
	@Output() public loadNewItems: EventEmitter<any> = new EventEmitter();
	@Input() public collectionItems: LibraryCollectionItemsAndFolders;
	@Input() public sortBy: string;
	@Input() public searchStr: string;
	@Input() public multiSelect: boolean;
	@Output() public setSelectAllChecked: EventEmitter<boolean> = new EventEmitter();
	@Input() public expandedHeight: number;
	@Input() public currentlySelected: any;
	@Output() public setActivityPanelIsOpen: EventEmitter<boolean> = new EventEmitter();
	@Input() public orgSettings: any;
	@Output() public setCurrentlySelected: EventEmitter<any> = new EventEmitter();

	@Input() public useTreeView = false;
	@Input() public nodeLevel = 0;

	constructor (
		private elementRef: ElementRef,
		@Inject(fullstoryToken) private fullstoryService: FullstoryService,
		private dialog: MatDialog,
		private libraryService: LibraryService,
		private translate: TranslateService,
		private searchPipe: SearchPipe,
		@Inject(userServiceToken) private userService: UserService,
		private modal: GoModalService,
		public dataSource: LibraryTreeDataSource
	) {}

	public ngOnInit (): void {
		if (this.useTreeView) {
			return;
		}

		this.filteredItems = this.collectionItems.items.filter((item) => {
			return item.library_collection_folder_id === this.folder.id;
		});
	}

	public isFolderLoading (): boolean {
		const folderNode = this.dataSource.data.find((node) =>
			node.type === 'folder' && (node.data as LibraryFolder).id === this.folder.id
		);
		return folderNode?.isLoading || false;
	}

	public ngOnChanges (changes) {
		if (this.useTreeView) {
			return;
		}

		if (changes.searchStr && !changes.searchStr.isFirstChange()) {
			this.elementRef.nativeElement.classList.remove('hidden');

			this.filteredItems = this.searchPipe.transform(this.filteredItems, changes.searchStr.currentValue, ['name', 'description', 'creator_name']);

			if (!this.emptyString(changes.searchStr.currentValue) && this.filteredItems.length === 0) {
				this.elementRef.nativeElement.classList.add('hidden');
			}

			if (!this.emptyString(changes.searchStr.currentValue) && this.filteredItems.length > 0 &&
				!this.isExpanded()) {
				this.expandFolder(this.folder);
			}

			if (
				!this.emptyString(changes.searchStr.previousValue) &&
				this.emptyString(changes.searchStr.currentValue) &&
				this.isExpanded()
			) {
				this.expandFolder(this.folder);
			}
		}
	}

	// TODO - LIB_FOLDERS (filtered items should be derived from datasource.connect)
	public updateItems () {
		if (this.useTreeView) {
			return;
		}

		this.filteredItems = this.collectionItems.items.filter((item) => {
			return item.library_collection_folder_id === this.folder.id;
		});
	}

	public managedCollectionAccess (): boolean {
		if (this.selectedCollection.type === LIBRARY_TYPES.MANAGED) {
			return this.userService.currentUser.is_root_content;
		}
		//if not a managed collection just return true
		return true;
	}

	public getAriaLabel (): string {
		return this.translate.instant('collection-folder-aria-label', {
			name: this.folder.name,
			date: this.formatDate(this.folder.updated_at)
		});
	}

	public formatDate (date) {
		return dayjs(date).format('MMM D, YYYY');
	}

	public getSortKey () {
		let itemField: string = 'name';

		if (this.sortBy === SORT_OPTIONS.ALPHANUMERICREVERSE) {
			itemField = '-name';
		} else if (this.sortBy === SORT_OPTIONS.LASTMODIFIED) {
			itemField = '-modified_at';
		}  else if (this.sortBy === SORT_OPTIONS.NEWEST) {
			itemField = '-created_at';
		} else if (this.sortBy === SORT_OPTIONS.OLDEST) {
			itemField = 'created_at';
		}

		return itemField;
	}

	public isExpanded (): boolean {
		if (this.useTreeView) {
			return this.dataSource.isFolderExpanded(this.folder.id);
		}
		return this.expandedFolders.includes(this.folder.id);
	}

	public expandFolder (folder: LibraryFolder): void {
		const id = folder.id;

		if (this.useTreeView) {
			if (!this.dataSource.isFolderExpanded(id)) {
				this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_FOLDER_OPENED, {});
			}
			this.dataSource.toggleFolder(id);
		} else {
			const expanded = this.expandedFolders;
			if (expanded.includes(id)) {
				expanded.splice(expanded.indexOf(id), 1);
			} else {
				this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_FOLDER_OPENED, {});
				expanded.push(id);
			}
		}
		this.setCurrentlySelected.emit({id});
	}

	public removeFolder (folder: LibraryFolder) {
		return this.libraryService.getCollectionFolderItemCount(folder.id).pipe(
			catchError(() => of(null)), // if item counts fail, continue with null
			switchMap((itemCounts) => {
				if (this.useTreeView && itemCounts?.folders > 0) {
					return this.dialog.open(
						MessageDialogComponent, {
							data: {
								title: this.translate.instant('modal-remove-collection-folder_title'),
								message: this.translate.instant('modal-remove-collection-folder_message-error', {
									folderCount: itemCounts.folders
								}),
								cancelText: this.translate.instant('common_cancel')
							} as MessageDialogData
						}).afterClosed().pipe(map(()=> false));
				} else {
					const message = this.buildConfirmationMessage(itemCounts);
					return this.dialog.open(
						ConfirmDialogComponent, {
							data: {
								title: this.translate.instant('modal-remove-collection-folder_title'),
								message,
								confirmText: this.translate.instant('common_remove'),
								cancelText: this.translate.instant('common_cancel')
							} as ConfirmDialogData
						}).afterClosed();
				}
			}),
			filter((confirm: boolean) => confirm),
			switchMap(() => this.libraryService.removeFolder(folder.id))
		).subscribe({
			next: () => {
				if (this.useTreeView) {
					this.dataSource.removeFolder(folder.id);
				} else {
					const index = this.collectionItems.folders.findIndex((each) => each.id === folder.id);
					this.collectionItems.folders.splice(index, 1);
					this.collectionItems.folders = [...this.collectionItems.folders];
				}
			},
			error: () => {
				this.dialog.open(MessageDialogComponent, {
					data: {
						title: this.translate.instant('modal-remove-collection-folder_error-title'),
						message: this.translate.instant('modal-remove-collection-folder_error-message'),
						confirmText: this.translate.instant('common_close')
					} as MessageDialogData
				});
			}
		});
	}

	public openEditFolderModal () {
		return this.modal.open(ManageLibraryCollectionFolderComponent, false, {
			data: {
				createMode: false,
				existingFolders: this.useTreeView ? [] : this.collectionItems.folders,
				folder: this.folder
			}
		}).afterClosed().subscribe((res) => {
			if (res.dismissed) {
				return;
			}
			if (res.request_failed) {
				return this.dialog.open(MessageDialogComponent, {
					data: {
						title: this.translate.instant('library-collections_edit-folder-fail-title'),
						message: this.translate.instant('library-collections_edit-folder-fail-message'),
						confirmText: this.translate.instant('common_close')
					} as MessageDialogData
				});
			}
			this.folder.name = res.name;
			this.folder.updated_at = res.updated_at;

			if (this.useTreeView) {
				this.dataSource.updateFolder({
					...this.folder
				});
			}
		});
	}

	private emptyString (value) {
		return value == null || value === '';
	}

	private buildConfirmationMessage (itemCounts: LibraryCollectionFolderItemCount): string {
		if (itemCounts) {
			const hasItems = itemCounts.media ||
				itemCounts.rubrics ||
				itemCounts.markers ||
				itemCounts.documents ||
				itemCounts.activities ||
				itemCounts.folders;

			if (hasItems) {
				return this.translate.instant('modal-remove-collection-folder_message-with-items', {
					media: itemCounts.media,
					rubrics: itemCounts.rubrics,
					markers: itemCounts.markers,
					documents: itemCounts.documents,
					activities: itemCounts.activities,
					folders: itemCounts.folders
				});
			}
		}

		return this.translate.instant('modal-remove-collection-folder_message');
	}
}
